/** Pure presentation contracts shared by Home and the alert landing page.
 * Follows own identity before ranking, novelty owns its own section, and only
 * Home limits follows. No horizon is ever applied to the full follow calendar.
 */
import { bandItemToFeedItem } from './eventCardShape';
import { feedItemKey } from './feedItemKey';
import { bucketByWeek } from './whatsOnBuckets';
import { MAX_ANCHOR_SECTIONS } from './whatsOnSections';
import { getLocalDateString } from './dateUtils';

export const HOME_LIMIT = 12;
export const HOME_PIN_LIMIT = 4;
export const HOME_SOURCE_LIMIT = 2;
export const PAGE_RANKED_LIMIT = 48;
export const HOME_AUDIENCE_MIN = 2;

/** Home mix for a family viewer: the best `budget` events, but at least `min`
 * for the kids and `min` for the adults when the pool has them, so Home is
 * never only kids' shows or only date nights. 'everyone' events (both
 * appeals high) count toward neither minimum; they rank well on their own.
 * A missing side's best reserve replaces the lowest-ranked pick that is not
 * itself protected, and rank order is kept. Pools without audience labels
 * (viewers without children) pass through as a plain top-`budget`.
 */
export function balanceAudiences(pool, budget, min = HOME_AUDIENCE_MIN) {
  const picked = pool.slice(0, budget);
  if (!picked.some(item => item.audience)) return picked;
  const count = side => picked.filter(item => item.audience === side).length;
  for (const side of ['kids', 'adults']) {
    const other = side === 'kids' ? 'adults' : 'kids';
    const reserves = pool.slice(budget).filter(item => item.audience === side);
    while (count(side) < min && reserves.length > 0) {
      let drop = -1;
      for (let k = picked.length - 1; k >= 0; k--) {
        const audience = picked[k].audience;
        if (audience === side || (audience === other && count(other) <= min)) continue;
        drop = k;
        break;
      }
      if (drop < 0) break;
      picked.splice(drop, 1);
      picked.push(reserves.shift());
    }
  }
  const order = new Map(pool.map((item, idx) => [item, idx]));
  return picked.sort((a, b) => order.get(a) - order.get(b));
}

export const isDismissed = (item, keys) => !!keys && (
  keys.has(feedItemKey(item)) || keys.has(item.id ?? item.content_id)
);
const dated = item => ({ ...item, event_date: item.event_date || item.start_date || item.date || null });
// Undated items stay: they are not known to be over.
const stillOn = (item, today) => {
  const last = String(item.end_date || item.event_date || '').slice(0, 10);
  return !last || !today || last >= today;
};

export function followFeedItems(newly, upcoming) {
  return [
    ...(newly?.groups || []).flatMap(group => (group.items || []).map(item => ({
      ...item, follow_source: item.follow_source || group.source
    }))),
    ...(upcoming?.items || [])
  ].map(item => dated(bandItemToFeedItem(item, { isNew: item.is_new_since_last_alert === true })));
}

function uniqueVisible(items, seen, dismissedKeys) {
  return items.filter(item => {
    if (isDismissed(item, dismissedKeys)) return false;
    const key = feedItemKey(item);
    if (key && seen.has(key)) return false;
    if (key) seen.add(key);
    return true;
  });
}
const section = (key, label, items, extra = {}) => ({
  key, id: `whats-on-${key}`, labelKey: `whatsOn.${label}`,
  count: items.length, items, ...extra
});

/** Pools must already be eligible. Fallback fills remaining slots only AFTER
 * identity, dismissal and Home time/source restrictions. Anchors arrive in
 * decision-deadline order as { anchor, items }.
 */
export function composeWhatsOnResult({
  newly, upcoming, followItems, ranked = [], farther = [], anchors = [],
  dismissedKeys = new Set(), today = getLocalDateString(new Date()),
  home = false, limit = HOME_LIMIT, pinLimit = HOME_PIN_LIMIT,
  rankedLimit = PAGE_RANKED_LIMIT
} = {}) {
  const seen = new Set();
  const followCandidates = followItems || followFeedItems(newly, upcoming);
  // The server keeps a show until its last LOCAL day has passed everywhere on
  // Earth (server/utils/eventDayFloor.js), so tonight's show west of UTC is not
  // lost — and east of UTC last night's show can still arrive the next morning.
  // Let it go by the viewer's own date. The cost is a viewer far east of a
  // followed venue losing that venue's show a few hours early, which beats a
  // finished card at the top of "This week".
  const allFollows = uniqueVisible(followCandidates.filter(item => stillOn(item, today)), seen, dismissedKeys);
  // Reserve even omitted Home follows: a source cap cannot be bypassed by the
  // same event returning as ranked content without its follow explanation.
  let follows = allFollows.slice().sort((a, b) =>
    String(a.event_date || '').localeCompare(String(b.event_date || '')));
  const homeWindow = items => {
    const buckets = bucketByWeek(items, today);
    return [...buckets.soon.items, ...buckets.nextWeekend.items];
  };
  if (home) {
    const sourceCounts = new Map();
    follows = homeWindow(follows).filter(item => {
      const source = item.follow_source;
      const key = `${source?.kind || 'venue'}:${source?.id || item.venue_id || feedItemKey(item)}`;
      const count = sourceCounts.get(key) || 0;
      if (count >= HOME_SOURCE_LIMIT) return false;
      sourceCounts.set(key, count + 1);
      return true;
    }).slice(0, Math.min(pinLimit, limit));
  }
  const budget = home ? Math.max(0, limit - follows.length) : rankedLimit;
  const primary = uniqueVisible(ranked.map(dated), seen, dismissedKeys);
  const fallback = uniqueVisible(farther.map(dated), seen, dismissedKeys);
  const primaryEligible = home ? homeWindow(primary) : primary;
  const fallbackEligible = home ? homeWindow(fallback) : fallback;
  const eligible = [...primaryEligible, ...fallbackEligible];
  const primaryEligibleSet = new Set(primaryEligible);
  const discovery = home ? balanceAudiences(eligible, budget) : eligible.slice(0, budget);
  const trueNew = home ? [] : follows.filter(item => item._isNew === true).sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
  const chronological = home ? follows : follows.filter(item => item._isNew !== true);
  const buckets = bucketByWeek([...chronological, ...discovery], today);
  const out = [
    section('newly', 'newHeading', trueNew),
    section('soon', buckets.soon.key, buckets.soon.items),
    section('nextWeekend', buckets.nextWeekend.key, buckets.nextWeekend.items),
    section('later', 'furtherOut', home ? [] : buckets.later.items)
  ].filter(s => s.count > 0);
  // Only rendered discovery owns an anchor duplicate. A candidate that lost
  // the discovery cap must not delete its book-ahead representation.
  const rendered = new Set([...allFollows, ...discovery].map(feedItemKey).filter(Boolean));
  const anchorSections = anchors.map(a => ({
    ...a, items: uniqueVisible(a.items.map(dated), rendered, dismissedKeys)
  })).filter(a => a.items.length > 0).slice(0, MAX_ANCHOR_SECTIONS);
  if (!home && anchorSections.length) {
    out.push(section('book-ahead', 'bookAheadHeading', anchorSections.flatMap(a => a.items), { anchorSections }));
  }
  return { sections: out, counts: {
    followCandidates: followCandidates.length, followAfterIdentityAndDismissal: allFollows.length,
    followAfterTimeAndHomeCaps: follows.length,
    primaryCandidates: ranked.length, primaryAfterIdentityAndDismissal: primary.length,
    primaryAfterTimeWindow: primaryEligible.length,
    fartherCandidates: farther.length, fartherAfterIdentityAndDismissal: fallback.length,
    fartherAfterTimeWindow: fallbackEligible.length,
    primaryRendered: discovery.filter(item => primaryEligibleSet.has(item)).length,
    fartherRendered: discovery.filter(item => !primaryEligibleSet.has(item)).length,
    anchorCandidates: anchors.reduce((n, a) => n + a.items.length, 0),
    anchorsAfterIdentityDismissalAndSectionCap: anchorSections.reduce((n, a) => n + a.items.length, 0)
  } };
}

export function composeWhatsOnSections(options) {
  return composeWhatsOnResult(options).sections;
}
